import { Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import {
  BooleanField,
  DateField,
  FunctionField,
  NumberField,
  ReferenceField,
  TextField,
  useRecordContext,
} from 'react-admin';

import { asDisplayString } from '@/formatting/text';
import type { FieldDescriptor } from '@/resources/descriptors';

import { Badge } from './kit';
import { fontStacks } from './tokens';

/**
 * Rendu en lecture d'un champ, deduit de son descripteur.
 *
 * Ce module est la raison pour laquelle ajouter une ressource ne demande pas
 * d'ecrire un ecran : le descripteur dit « millilitres », le tableau affiche
 * « 120 ml ». Une unite affichee est de l'information ; un nombre nu en est
 * moins, et un nombre nu dans une colonne « Poids » est une invitation a l'erreur
 * — grammes ou kilogrammes ?
 */

/** Unite affichee apres la valeur, par nature de champ. */
const UNIT_BY_KIND: Partial<Record<FieldDescriptor['kind'], string>> = {
  milliliters: 'ml',
  grams: 'g',
  millimeters: 'mm',
  centimeters: 'cm',
  celsius: '°C',
  months: 'mois',
};

/**
 * Duree lisible.
 *
 * `1830 s` ne se lit pas ; « 30 min 30 s » se lit. Les secondes sont conservees
 * sous la minute, ou elles sont l'unite naturelle d'une teteee courte.
 */
export const formatDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '—';
  }

  const rounded = Math.round(seconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const remaining = rounded % 60;

  if (hours > 0) {
    return `${String(hours)} h ${String(minutes).padStart(2, '0')} min`;
  }

  if (minutes > 0) {
    return remaining === 0
      ? `${String(minutes)} min`
      : `${String(minutes)} min ${String(remaining).padStart(2, '0')} s`;
  }

  return `${String(rounded)} s`;
};

/** Libelle d'une valeur de liste de choix, ou la valeur brute si elle est inconnue. */
export const choiceLabel = (descriptor: FieldDescriptor, value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return '—';
  }

  const match = descriptor.choices?.find((choice) => choice.id === asDisplayString(value));

  // Une valeur absente du catalogue est affichee telle quelle plutot que masquee :
  // c'est le signe que l'enum du backend a bouge et que le catalogue doit suivre.
  return match?.name ?? asDisplayString(value);
};

/** Identifiant technique : lisible mais visiblement secondaire. */
const IdentifierField = ({ source }: { source: string }): ReactElement => (
  <FunctionField
    source={source}
    render={(record: Record<string, unknown>) => {
      const value = record[source];

      return (
        <Typography variant="body2" sx={{ fontFamily: fontStacks.mono, fontSize: '0.8rem', color: 'text.secondary' }}>
          {typeof value === 'string' && value !== '' ? value : '—'}
        </Typography>
      );
    }}
  />
);

/** Liste de chaines : etiquettes plutot qu'un `a,b,c` illisible. */
const StringListField = ({ source }: { source: string }): ReactElement => {
  const record = useRecordContext<Record<string, unknown>>();
  const value = record?.[source];

  if (!Array.isArray(value) || value.length === 0) {
    return <Typography variant="body2">—</Typography>;
  }

  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {value.map((entry, index) => (
        <Badge key={`${String(entry)}-${String(index)}`} label={String(entry)} />
      ))}
    </Stack>
  );
};

/**
 * Objet imbrique (etat de conservation, usages de stock).
 *
 * Affiche en JSON compact et non deplie : ces objets sont un detail d'audit, et
 * leur donner une mise en page complete volerait la place des colonnes utiles.
 */
const JsonField = ({ source }: { source: string }): ReactElement => {
  const record = useRecordContext<Record<string, unknown>>();
  const value = record?.[source];

  if (value === undefined || value === null) {
    return <Typography variant="body2">—</Typography>;
  }

  return (
    <Typography
      variant="body2"
      component="pre"
      sx={{
        fontFamily: fontStacks.mono,
        fontSize: '0.75rem',
        margin: 0,
        maxWidth: 360,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Typography>
  );
};

/**
 * Champ de lecture correspondant au descripteur.
 *
 * `sortable` est pilote par l'appelant : les ressources paginees par le serveur
 * ne savent pas trier, et proposer un en-tete cliquable qui ne trie que la page
 * courante serait un mensonge visuel.
 */
export const renderField = (descriptor: FieldDescriptor, sortable: boolean): ReactElement => {
  const common = { source: descriptor.name, label: descriptor.label, sortable };
  const unit = UNIT_BY_KIND[descriptor.kind];

  switch (descriptor.kind) {
    case 'identifier':
      return <IdentifierField key={descriptor.name} source={descriptor.name} />;

    case 'boolean':
      return <BooleanField key={descriptor.name} {...common} />;

    case 'date':
      return <DateField key={descriptor.name} {...common} locales="fr-FR" />;

    case 'dateTime':
      return <DateField key={descriptor.name} {...common} locales="fr-FR" showTime />;

    case 'choice':
      return (
        <FunctionField
          key={descriptor.name}
          {...common}
          render={(record: Record<string, unknown>) => choiceLabel(descriptor, record[descriptor.name])}
        />
      );

    case 'reference':
      // Sans ressource cible declaree, un `ReferenceField` planterait au montage :
      // on retombe sur l'identifiant brut, qui reste exploitable.
      return descriptor.reference === undefined ? (
        <IdentifierField key={descriptor.name} source={descriptor.name} />
      ) : (
        <ReferenceField
          key={descriptor.name}
          {...common}
          reference={descriptor.reference}
          link="show"
          empty="—"
        />
      );

    case 'stringList':
      return <StringListField key={descriptor.name} source={descriptor.name} />;

    case 'json':
      return <JsonField key={descriptor.name} source={descriptor.name} />;

    case 'seconds':
      return (
        <FunctionField
          key={descriptor.name}
          {...common}
          render={(record: Record<string, unknown>) => {
            const value = record[descriptor.name];

            return typeof value === 'number' ? formatDuration(value) : '—';
          }}
        />
      );

    case 'integer':
    case 'milliliters':
    case 'grams':
    case 'millimeters':
    case 'celsius':
    case 'months':
      return unit === undefined ? (
        <NumberField key={descriptor.name} {...common} emptyText="—" />
      ) : (
        <FunctionField
          key={descriptor.name}
          {...common}
          render={(record: Record<string, unknown>) => {
            const value = record[descriptor.name];

            return typeof value === 'number'
              ? `${value.toLocaleString('fr-FR')} ${unit}`
              : '—';
          }}
        />
      );

    // Stocke en millimetres, affiche en centimetres (#373) : seul kind qui
    // convertit la valeur plutot que de simplement lui accoler une unite.
    case 'centimeters':
      return (
        <FunctionField
          key={descriptor.name}
          {...common}
          render={(record: Record<string, unknown>) => {
            const value = record[descriptor.name];

            return typeof value === 'number'
              ? `${(value / 10).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} cm`
              : '—';
          }}
        />
      );

    case 'longText':
      return (
        <FunctionField
          key={descriptor.name}
          {...common}
          render={(record: Record<string, unknown>) => {
            const value = record[descriptor.name];

            if (typeof value !== 'string' || value === '') {
              return '—';
            }

            // Une note de plusieurs lignes deformerait la hauteur des lignes du
            // tableau : elle est tronquee ici, et lisible en entier sur la fiche.
            return value.length > 80 ? `${value.slice(0, 80)}…` : value;
          }}
        />
      );

    case 'text':
    case 'email':
      return <TextField key={descriptor.name} {...common} emptyText="—" />;
  }
};
